/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = 2;
    }
    connectedCallback() {
      /* KL nav-build-c: in oldest-first mode hold every load until facets.js
         (KlOrder) has swapped the last page in and set the page counter —
         otherwise the first observer tick could fetch page 2 in newest order. */
      if (document.documentElement.classList.contains('kl-order-oldest')) {
        this.klOldest = true;
        this.klHold = true;
      }
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
        // first reserve at page load (the grid's first page is on screen, the loader is not): see klReserve
        if (this.grid && Number(this.grid.dataset.klPages) > 1) requestAnimationFrame(() => this.klReserveWhenOffscreen());
        /* KL nav-build-c: the observer only fires on an exact crossing, so a
           fast scroll that jumps past the loader never loads. A throttled
           scroll check via maybeLoadNext (which already guards loading,
           done and position) closes that gap in both orders. */
        let base = this, ticking = false;
        base.klScrollHandler = function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () { ticking = false; if (base.klPendingReserve) base.klReserveWhenOffscreen(); base.maybeLoadNext(); });
        };
        window.addEventListener('scroll', base.klScrollHandler, { passive: true });
      }
    }
    disconnectedCallback() {
      if (this.klScrollHandler) {
        window.removeEventListener('scroll', this.klScrollHandler);
      }
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function () {
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    /* KL (2026-08-22, HOD follow-up defect 1): the observer only fires on
       a ratio-1 CROSSING. If that crossing happens while 'loading' is
       still set (the gsap append animation holds it ~0.75s), the event is
       swallowed and nothing ever re-arms — at normal phone scroll speed
       the catalog dead-ends mid-way with the loader stranded above the
       viewport. After every completed append, re-check: if the loader
       sits at or above the fold, keep loading until it drops below it or
       the catalog ends. */
    klShowLoadMore() {
      let base = this;
      if (!base.button) {
        return;
      }
      base.button.classList.remove('visually-hidden');
      base.classList.add('kl-loadmore-visible');
      if (!base.klLoadMoreWired) {
        base.klLoadMoreWired = true;
        base.button.addEventListener('click', function () {
          base.classList.remove('kl-loadmore-visible');
          base.klRetries = 0;
          base.klDone = false;
          if (base.observer) {
            base.observer.observe(base);
          }
          base.loadProducts();
          this.blur();
        });
      }
    }
    maybeLoadNext() {
      let base = this;
      if (base.klDone || !base.classList.contains('pagination-type--infinite')) {
        return;
      }
      if (base.getBoundingClientRect().top <= window.innerHeight) {
        setTimeout(function () { base.loadProducts(); }, 400);
      }
    }
    loadProducts() {
      let base = this;
      /* KL nav-build-c (requirement 01): "oldest first" is the collection's
         newest-first order walked backwards. facets.js swaps in the LAST page
         and sets klOldest + i = last page - 1; from there each load fetches the
         previous page and stops after page 1. */
      if (base.klOldest && base.i < 1) {
        base.klRelease();   // HOD r4 B-8: nothing more to come — give the held space back
        base.klDone = true;
        if (base.observer) {
          base.observer.unobserve(base);
        }
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      if (base.klHold) {
        return;
      }
      let url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
      if (base.getAttribute('loading') || base.klDone) {
        return;
      }
      base.i += base.klOldest ? -1 : 1;
      base.setAttribute('loading', true);
      if (base.classList.contains('pagination-type--infinite') && (!base.klSpacer || !base.klSpacer.parentNode)) base.klReserveWhenOffscreen();
      fetch(url)
        .then(response => response.text())
        .then((responseText) => {
          const html = responseText;
          base.renderProducts(html, url);

          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        })
        .catch(function () {
          /* HOD q12: a REJECTED fetch (a real dropped connection — the
             commonest phone failure) used to bypass the whole ladder:
             'loading' stuck forever, no retries, no button, page wedged
             at a spinner until reload. Rejections now run the exact same
             failure ladder as a bad response body. */
          base.klHandleLoadFailure();
        });
    }
    /* Mobile audit 4 Oct 2026 (item 4, Brent: "fix"): while the next batch is on its way, hold the space it will fill —
       as many rows as one page of cards takes, at the current card height — so the collections band, the strip and the
       footer below the grid do not jump down when the cards arrive (Chrome measured the All page at 4-5 jumps' worth).
       The space is released the moment the cards are in (they fill it) or the catalog ends. */
    klReserve() {
      let base = this, g = base.grid;
      if (!g) return;
      let cards = g.querySelectorAll('.column');
      if (cards.length < 2) return;
      if (!base.klPerPage) base.klPerPage = cards.length;
      let firstTop = cards[0].getBoundingClientRect().top, cols = 0, secondRowTop = null;
      for (let c of cards) { let t = c.getBoundingClientRect().top; if (Math.abs(t - firstTop) < 2) cols++; else { secondRowTop = t; break; } }
      let cardH = cards[cards.length - 1].getBoundingClientRect().height;
      let pitch = secondRowTop !== null ? (secondRowTop - firstTop) : cardH;
      let rows = Math.ceil(base.klPerPage / Math.max(cols, 1));
      // a spacer AFTER the grid, never a min-height ON it: a taller grid box re-spaces the rows already in it
      // (the grid's own alignment stretches them), which registered as a far bigger jump than the one being cured
      if (!base.klSpacer) { base.klSpacer = document.createElement('div'); base.klSpacer.className = 'kl-load-reserve'; base.klSpacer.setAttribute('aria-hidden', 'true'); }
      base.klSpacer.style.height = Math.round(rows * pitch) + 'px';
      // HOD r3 B-6: the spacer goes AFTER the loader, never between the grid and the loader — there it kept the loader (and so
      // the loading) 1790px away while the shopper looked at white; loading now starts exactly where live starts it
      if (!base.klSpacer.parentNode) base.parentNode.insertBefore(base.klSpacer, base.nextSibling);
    }
    /* reserve only while the loader is below the screen (a reserve made while it is in view would itself be the jump);
       otherwise wait for the next scroll tick that puts it below */
    klReserveWhenOffscreen() {
      let base = this;
      if (base.getBoundingClientRect().bottom > window.innerHeight) { base.klPendingReserve = false; base.klReserve(); return; }
      base.klPendingReserve = true;
    }
    klRelease() { this.klPendingReserve = false; if (this.klSpacer && this.klSpacer.parentNode) this.klSpacer.parentNode.removeChild(this.klSpacer); }
    /* is there a page still to fetch? (newest-first: the next page number is within the count; oldest-first: page 1 not yet taken) */
    klMorePages() {
      if (this.klDone) return false;
      const pages = Number(this.grid && this.grid.dataset.klPages) || 0;
      return this.klOldest ? this.i >= 1 : (!pages || this.i <= pages);
    }
    klHandleLoadFailure() {
      let base = this;
      base.klRelease();
      base.klRetries = (base.klRetries || 0) + 1;
      base.i += base.klOldest ? 1 : -1;
      base.removeAttribute('loading');
      if (base.klRetries < 5) {
        setTimeout(function () { base.maybeLoadNext(); }, 2500 * base.klRetries);
        return;
      }
      base.klDone = true;
      if (base.observer) {
        base.observer.unobserve(base);
      }
      base.klShowLoadMore();
    }
    renderProducts(html, url) {
      let base = this,
        grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');

      if (!grid_to_replace) {
        /* KL (2026-08-22, CARD-TRIAD follow-up): stock behaviour gave up
           FOREVER on the first bad response — one hiccup on a shaky
           connection and the rest of the catalog became unreachable for
           the whole visit. Retry up to 3 times (the page number was
           already advanced, so rewind it); only then stop. */
        /* bad response body (challenge page, error page): same ladder
           as a rejected fetch — see klHandleLoadFailure. */
        base.klHandleLoadFailure();
        return;
      }
      base.klRetries = 0;
      let products = grid_to_replace.querySelectorAll('.column');

      /* KL: end-of-catalog guards for the drain loop — an empty page, or
         the same first product as the previous batch (out-of-range pages
         can echo the last page), both mean done. */
      let firstLink = products.length ? (products[0].querySelector('a') || {}).href || '' : '';
      if (!products.length || (firstLink && firstLink === base.klLastFirstLink)) {
        base.klRelease();
        base.klDone = true;
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.removeAttribute('loading');
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      base.klLastFirstLink = firstLink;

      /* KL nav-build-c: in oldest-first mode each server page arrives
         newest-first; append it reversed so the wall keeps ascending. */
      if (base.klOldest) {
        products = Array.prototype.slice.call(products).reverse();
      }
      for (var i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
        products[i].querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      }
      base.klRelease();   // the cards now fill the space that was held for them
      // …and the space for the NEXT batch is held right away, while it is still below the screen — so the only move
      // happens off-screen, and the loader below it comes into view with its room already made
      if (base.klMorePages() && base.classList.contains('pagination-type--infinite')) base.klReserveWhenOffscreen();
      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.removeAttribute('loading');
            base.maybeLoadNext();
          }
        });
      } else {
        base.removeAttribute('loading');
        base.maybeLoadNext();
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}